import type { ReactNode } from 'react';
import { Badge } from '../ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { cn } from '../../lib/utils';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '../resource-preview/ResourcePreviewCard';
import { ResourcePreview } from '../resource-preview/ResourcePreview';

export interface EntityChipProps {
  /** Resource-preview kind resolved by the host registry (e.g. `models`). */
  kind: string;
  /** Stable id passed to the preview component (model id, provider id, agent type). */
  id: string;
  /** Leading brand/glyph node rendered before the label. */
  icon: ReactNode;
  /** Human-readable label shown in the chip. */
  label: string;
  /** Render the label in a monospace font (useful for raw ids). */
  mono?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Compact, presentational chip with a hover preview. Shared base for
 * {@link AgentTypeChip}, {@link ModelChip}, and {@link ProviderChip}. The
 * trigger renders a brand icon + label; hovering opens the registry-driven
 * {@link ResourcePreview} for `kind`/`id`. Pure render — no data fetching of
 * its own, so it is safe to use in both the host app and plugins.
 */
export function EntityChip({
  kind,
  id,
  icon,
  label,
  mono,
  className,
  'data-testid': dataTestId,
}: EntityChipProps) {
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`Preview ${kind} ${label}`}
          data-testid={dataTestId ?? `entity-chip-${kind}-${id}`}
          title={label}
          // The chip click is a no-op (the preview opens on hover), so stop
          // propagation to keep it safe inside clickable rows/cards that would
          // otherwise navigate when the chip is clicked.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="nodrag nopan inline-flex min-w-0 max-w-full items-center rounded-md align-middle transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge
            variant="outline"
            // `shrink`/`min-w-0` override the badge default `shrink-0` so a long
            // label truncates inside a narrow container instead of overflowing it.
            className={cn(
              'cursor-default gap-1 px-1.5 py-0 text-[10px] font-medium min-w-0 max-w-full shrink',
              className,
            )}
          >
            {icon}
            <span className={cn('max-w-[16rem] truncate', mono && 'font-mono')}>{label}</span>
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={`${kind} ${label}`}
      >
        <ResourcePreview kind={kind} id={id} fallbackName={label} />
      </HoverCardContent>
    </HoverCard>
  );
}
